import { Injectable } from '@angular/core';
import { DataService } from '../../../data.service';
import { NavigationLibraryService } from '../navigation/navigation.service';

export interface FocusRestorationOptions {
  containerSelector: string;
  fallbackContainerSelector?: string;
  resetFocusPosition?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FocusRestorationService {
  constructor(
    private readonly navService: NavigationLibraryService,
    private readonly dataService: DataService
  ) {}

  /**
   * Removes period symbols from CSS selectors
   * @param selector The CSS selector to clean
   * @returns The cleaned selector without period symbols
   */
  private cleanSelector(selector: string): string {
    return selector.replace(/\./g, '');
  }

  /**
   * Utility method to scroll to a specific row in the grid
   * @param verticalIndex The vertical index of the row to scroll to
   */
  scrollToRow(verticalIndex: number): void {
    const focusableRow = document.getElementsByClassName(
      'vertical-carousel-row-' + verticalIndex
    )[0];
    if (focusableRow) {
      const container = document.querySelector('.items-wrapper') as HTMLElement;
      if (container) {
        const rowRect = focusableRow.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTop = rowRect.top - containerRect.top + container.scrollTop;
        container.scrollTop = scrollTop;
      }
    }
  }

  /**
   * Restores focus to the last focused element in a grid layout
   * @param options Configuration options for focus restoration
   */
  restoreGridFocus(options: FocusRestorationOptions): void {
    const {
      containerSelector,
      fallbackContainerSelector,
      resetFocusPosition = false,
    } = options;

    // Try the primary container first
    let container = document.querySelector(containerSelector);

    // If primary container not found and fallback is provided, try fallback
    if (!container && fallbackContainerSelector) {
      container = document.querySelector(fallbackContainerSelector);
    }

    if (!container) {
      console.warn(
        `Focus restoration: Container not found for selector: ${containerSelector}`
      );
      return;
    }

    const rows = container.getElementsByClassName('grid-container-row');
    const { verticalIndex, horizontalIndex } =
      this.dataService.gridFocusPosition;

    if (rows[verticalIndex]) {
      const focusableElements =
        rows[verticalIndex].getElementsByClassName('focusable-element');

      if (focusableElements[horizontalIndex]) {
        this.navService.focusElement(
          focusableElements[horizontalIndex] as HTMLElement,
          horizontalIndex,
          verticalIndex,
          0
        );
        // Scroll to the row containing the focused element
        this.scrollToRow(verticalIndex);

        // Reset focus position if requested
        if (resetFocusPosition) {
          this.dataService.gridFocusPosition = {
            verticalIndex: 0,
            horizontalIndex: 0,
          };
        }
      } else {
        console.warn(
          `Focus restoration: Focusable element not found at position [${verticalIndex}, ${horizontalIndex}]`
        );
      }
    } else {
      console.warn(
        `Focus restoration: Row not found at index: ${verticalIndex}`
      );
    }
  }

  /**
   * Restores focus to a specific container
   * @param containerSelector The CSS selector for the container to focus
   * @param delay Optional delay before focusing
   */
  restoreContainerFocus(containerSelector: string, delay: number = 0): void {
    const container = document.querySelector(containerSelector);
    if (container) {
      if (delay > 0) {
        setTimeout(() => {
          this.navService.focusContainer(
            this.cleanSelector(containerSelector),
            false,
            0
          );
        }, delay);
      } else {
        this.navService.focusContainer(
          this.cleanSelector(containerSelector),
          false,
          0
        );
      }
    } else {
      console.warn(
        `Focus restoration: Container not found for selector: ${containerSelector}`
      );
    }
  }

  /**
   * Restores focus with fallback logic (e.g., keypad first, then grid)
   * @param primarySelector Primary container to try first
   * @param fallbackOptions Fallback options if primary container is not found
   */
  restoreFocusWithFallback(
    primarySelector: string,
    fallbackOptions: FocusRestorationOptions
  ): void {
    const primaryContainer = document.querySelector(primarySelector);
    if (primaryContainer) {
      this.navService.focusContainer(
        this.cleanSelector(primarySelector),
        false,
        0
      );
    } else {
      this.restoreGridFocus(fallbackOptions);
    }
  }

  resetIndex() {
    this.dataService.gridFocusPosition = {
      verticalIndex: 0,
      horizontalIndex: 0,
    };
  }
}
